/**
 * <ApexCard /> — Settings card for Apex 2.0 (AI Voice Lead Manager).
 *
 * Endpoints:
 *   GET   /api/apex/settings
 *   PATCH /api/apex/settings
 *   POST  /api/apex/test (dry-run summarizer)
 *
 * Phase 1 scope: enable/disable, always-on vs after-hours-only, custom
 * greeting, business-hours, Polly voice selection. (Phase 2 will add
 * Google Calendar booking + ElevenLabs.)
 */
import { useEffect, useRef, useState } from "react";
import { Loader2, Phone, Save, Sparkles, FlaskConical, CheckCircle2, Calendar, ExternalLink, Unlink, Play, Lock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const POLLY_VOICES = [
  { id: "Polly.Joanna-Neural", label: "Joanna (US Female · warm)" },
  { id: "Polly.Matthew-Neural", label: "Matthew (US Male · steady)" },
  { id: "Polly.Salli-Neural", label: "Salli (US Female · upbeat)" },
  { id: "Polly.Joey-Neural", label: "Joey (US Male · friendly)" },
  { id: "Polly.Kendra-Neural", label: "Kendra (US Female · pro)" },
];

const HOURS = Array.from({ length: 24 }, (_, i) => i);

export default function ApexCard() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [gcal, setGcal] = useState(null);
  const [connecting, setConnecting] = useState(false);

  const loadGcalStatus = () =>
    api.get("/oauth/calendar/status").then((r) => setGcal(r.data)).catch(() => setGcal(null));

  useEffect(() => {
    api.get("/apex/settings").then((r) => setS(r.data))
      .catch(() => setS(null))
      .finally(() => setLoading(false));
    loadGcalStatus();
    // If we landed back from OAuth with ?gcal=connected, toast it.
    const params = new URLSearchParams(window.location.search);
    if (params.get("gcal") === "connected") {
      toast.success("Google Calendar connected!");
      // Strip the query string so a refresh doesn't re-toast.
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("gcal") === "error") {
      toast.error(`Calendar connection failed: ${params.get("reason") || "unknown"}`);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const set = (patch) => setS((prev) => ({ ...prev, ...patch }));

  const onSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/apex/settings", {
        enabled: s.enabled,
        always_on: s.always_on,
        business_hours_start: Number(s.business_hours_start),
        business_hours_end: Number(s.business_hours_end),
        timezone_offset: Number(s.timezone_offset),
        greeting: s.greeting,
        offhours_greeting: s.offhours_greeting,
        voice: s.voice,
        booking_enabled: !!s.booking_enabled,
        appointment_duration_min: Number(s.appointment_duration_min) || 15,
        auto_stage_enabled: !!s.auto_stage_enabled,
        auto_stage_threshold: Number(s.auto_stage_threshold) || 0.7,
        dormancy_sweep_enabled: !!s.dormancy_sweep_enabled,
        dormancy_days: Number(s.dormancy_days) || 7,
        voice_provider: s.voice_provider || "polly",
        elevenlabs_voice_key: s.elevenlabs_voice_key || null,
      });
      setS(data);
      toast.success("Apex 2.0 settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      // A canned 5-slot transcript so the user can sanity-check Claude
      // before pointing real traffic at the agent.
      const fakeTranscript = [
        { role: "assistant", text: "What's the property address?" },
        { role: "user", text: "1408 Magnolia Street in Houston Texas 77002" },
        { role: "assistant", text: "Why selling?" },
        { role: "user", text: "Just inherited it and don't want to deal with it" },
        { role: "assistant", text: "Timeline?" },
        { role: "user", text: "ASAP — within two weeks if possible" },
        { role: "assistant", text: "Asking price?" },
        { role: "user", text: "Around 165 thousand" },
        { role: "assistant", text: "Condition?" },
        { role: "user", text: "Rough — vacant for a year, needs full rehab" },
      ];
      const { data } = await api.post("/apex/test", { transcript: fakeTranscript });
      setTestResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dry-run failed");
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="card p-6 text-center">
        <Loader2 size={16} className="animate-spin inline" /> Loading Apex 2.0…
      </div>
    );
  }
  if (!s) return null;

  return (
    <div className="card" data-testid="apex-card">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="label-eyebrow flex items-center gap-1.5">
            <Sparkles size={11} className="text-indigo-600" /> AI Voice Lead Manager
          </div>
          <h3 className="font-display font-bold text-xl mt-1 flex items-center gap-2">
            <Phone size={18} className="text-indigo-600" /> Apex 2.0
          </h3>
          <p className="text-xs text-[var(--ink-3)] mt-1 max-w-xl">
            Apex answers inbound seller calls, runs the qualification script, and drops a fully-scored
            lead into your pipeline. Works alongside your Twilio number — no extra setup.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="apex-enabled-toggle">
          <input
            type="checkbox"
            checked={!!s.enabled}
            onChange={(e) => set({ enabled: e.target.checked })}
            className="sr-only peer"
          />
          <span className="w-11 h-6 bg-slate-300 peer-checked:bg-emerald-500 rounded-full relative transition-colors">
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${s.enabled ? "translate-x-5" : ""}`} />
          </span>
          <span className="text-sm font-semibold">{s.enabled ? "On" : "Off"}</span>
        </label>
      </div>

      {/* When to answer */}
      <div className="grid md:grid-cols-2 gap-3 mb-4" data-testid="apex-when-section">
        <button
          type="button"
          onClick={() => set({ always_on: true })}
          className={`text-left p-4 rounded-lg border-2 transition-all ${s.always_on ? "border-indigo-500 bg-indigo-50/60" : "border-slate-200 bg-white hover:border-slate-300"}`}
          data-testid="apex-mode-always"
        >
          <div className="font-bold text-sm">Always answer</div>
          <div className="text-xs text-[var(--ink-3)] mt-0.5">Apex takes every inbound call — even during business hours.</div>
        </button>
        <button
          type="button"
          onClick={() => set({ always_on: false })}
          className={`text-left p-4 rounded-lg border-2 transition-all ${!s.always_on ? "border-indigo-500 bg-indigo-50/60" : "border-slate-200 bg-white hover:border-slate-300"}`}
          data-testid="apex-mode-afterhours"
        >
          <div className="font-bold text-sm">After-hours only</div>
          <div className="text-xs text-[var(--ink-3)] mt-0.5">Apex only answers when you're closed; you still ring during business hours.</div>
        </button>
      </div>

      {!s.always_on && (
        <div className="grid grid-cols-3 gap-3 mb-4" data-testid="apex-hours-section">
          <div>
            <label className="field-label">Open at</label>
            <select className="input" value={s.business_hours_start}
              onChange={(e) => set({ business_hours_start: Number(e.target.value) })}
              data-testid="apex-hours-start"
            >
              {HOURS.map((h) => <option key={h} value={h}>{`${h.toString().padStart(2, "0")}:00`}</option>)}
            </select>
          </div>
          <div>
            <label className="field-label">Close at</label>
            <select className="input" value={s.business_hours_end}
              onChange={(e) => set({ business_hours_end: Number(e.target.value) })}
              data-testid="apex-hours-end"
            >
              {HOURS.map((h) => <option key={h} value={h}>{`${h.toString().padStart(2, "0")}:00`}</option>)}
            </select>
          </div>
          <div>
            <label className="field-label">UTC offset</label>
            <select className="input" value={s.timezone_offset}
              onChange={(e) => set({ timezone_offset: Number(e.target.value) })}
              data-testid="apex-tz-offset"
            >
              {[-10, -9, -8, -7, -6, -5, -4, -3, 0, 1, 5, 8].map((o) => (
                <option key={o} value={o}>{`UTC${o >= 0 ? "+" : ""}${o}`}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Voice */}
      <div className="mb-4">
        <ApexVoiceSection s={s} set={set} />
      </div>

      {/* Greeting */}
      <div className="mb-4">
        <label className="field-label">
          {s.always_on ? "Greeting" : "Business-hours greeting (unused if 'After-hours only')"}
        </label>
        <textarea className="textarea" rows={2} value={s.greeting}
          onChange={(e) => set({ greeting: e.target.value })}
          data-testid="apex-greeting"
        />
      </div>

      {!s.always_on && (
        <div className="mb-4">
          <label className="field-label">After-hours greeting</label>
          <textarea className="textarea" rows={2} value={s.offhours_greeting}
            onChange={(e) => set({ offhours_greeting: e.target.value })}
            data-testid="apex-offhours-greeting"
          />
        </div>
      )}

      {/* Phase 2 — Appointment booking */}
      <div className="mb-4 p-3 rounded-lg border border-indigo-100 bg-indigo-50/40" data-testid="apex-booking-section">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-bold text-indigo-700 flex items-center gap-1">
              <Calendar size={11} /> Phase 2 · Appointment booking
            </div>
            <div className="font-bold text-sm mt-0.5">Let Apex book appointments on your calendar</div>
            <div className="text-[11px] text-[var(--ink-3)]">
              After qualification, Apex offers 3 time slots and books the winner straight onto your Google Calendar.
            </div>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer shrink-0" data-testid="apex-booking-toggle">
            <input type="checkbox" className="sr-only peer"
              checked={!!s.booking_enabled}
              onChange={(e) => set({ booking_enabled: e.target.checked })}
            />
            <span className="w-9 h-5 bg-slate-300 peer-checked:bg-emerald-500 rounded-full relative transition-colors">
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${s.booking_enabled ? "translate-x-4" : ""}`} />
            </span>
          </label>
        </div>

        {/* Connect / status */}
        <div className="flex items-center gap-2 flex-wrap" data-testid="apex-gcal-row">
          {gcal?.connected ? (
            <>
              <span className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-300">
                <CheckCircle2 size={11} /> Google Calendar connected
              </span>
              {gcal.email && <span className="text-[11px] text-[var(--ink-3)] font-mono truncate">{gcal.email}</span>}
              <button type="button"
                className="btn-ghost text-xs"
                onClick={async () => {
                  await api.post("/oauth/calendar/disconnect").catch(() => {});
                  await loadGcalStatus();
                  toast.success("Disconnected");
                }}
                data-testid="apex-gcal-disconnect">
                <Unlink size={11} /> Disconnect
              </button>
            </>
          ) : gcal?.configured ? (
            <button type="button"
              className="btn-money"
              disabled={connecting}
              onClick={async () => {
                setConnecting(true);
                try {
                  const { data } = await api.get("/oauth/calendar/url");
                  window.location.href = data.authorization_url;
                } catch (e) {
                  toast.error("Failed to start Google sign-in");
                  setConnecting(false);
                }
              }}
              data-testid="apex-gcal-connect">
              {connecting ? <Loader2 size={14} className="animate-spin" /> : <Calendar size={14} />}
              {connecting ? "Redirecting…" : "Connect Google Calendar"}
              <ExternalLink size={11} />
            </button>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md bg-amber-50 text-amber-800 border border-amber-200"
                  data-testid="apex-gcal-not-configured">
              ⚠️ Google OAuth not configured — admin must set GOOGLE_CLIENT_ID / SECRET in backend env. Local-only bookings still work.
            </span>
          )}
        </div>

        {s.booking_enabled && (
          <div className="mt-3 grid grid-cols-2 gap-3" data-testid="apex-booking-duration-row">
            <div>
              <label className="field-label">Appointment length (min)</label>
              <select className="input" value={s.appointment_duration_min || 15}
                onChange={(e) => set({ appointment_duration_min: Number(e.target.value) })}
                data-testid="apex-appointment-duration"
              >
                {[10, 15, 20, 30, 45, 60].map((m) => <option key={m} value={m}>{`${m} min`}</option>)}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Phase 3a — Auto stage moves */}
      <div className="mb-4 p-3 rounded-lg border border-emerald-100 bg-emerald-50/40" data-testid="apex-autostage-section">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 flex items-center gap-1">
              <Sparkles size={11} /> Phase 3a · Auto stage moves
            </div>
            <div className="font-bold text-sm mt-0.5">Let Claude move leads through your pipeline automatically</div>
            <div className="text-[11px] text-[var(--ink-3)]">
              After every Apex call or inbound SMS, Claude classifies the signal and — if confident enough — moves the lead to the right stage. Every move is logged and one-click reversible.
            </div>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer shrink-0" data-testid="apex-autostage-toggle">
            <input type="checkbox" className="sr-only peer"
              checked={!!s.auto_stage_enabled}
              onChange={(e) => set({ auto_stage_enabled: e.target.checked })}
            />
            <span className="w-9 h-5 bg-slate-300 peer-checked:bg-emerald-500 rounded-full relative transition-colors">
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${s.auto_stage_enabled ? "translate-x-4" : ""}`} />
            </span>
          </label>
        </div>

        {s.auto_stage_enabled && (
          <div className="mt-3" data-testid="apex-autostage-threshold-row">
            <label className="field-label flex items-center justify-between">
              <span>Confidence threshold</span>
              <span className="text-emerald-700 font-bold tabular-nums">
                {Math.round((s.auto_stage_threshold || 0.7) * 100)}%
              </span>
            </label>
            <input type="range" min="50" max="95" step="5"
              value={Math.round((s.auto_stage_threshold || 0.7) * 100)}
              onChange={(e) => set({ auto_stage_threshold: Number(e.target.value) / 100 })}
              className="w-full accent-emerald-600"
              data-testid="apex-autostage-threshold"
            />
            <div className="text-[10px] text-[var(--ink-3)] flex justify-between mt-0.5">
              <span>50% — Aggressive</span>
              <span>95% — Conservative</span>
            </div>
          </div>
        )}
      </div>

      {/* Phase 3b — Dormancy sweep + outbound dialer */}
      <div className="mb-4 p-3 rounded-lg border border-rose-100 bg-rose-50/40" data-testid="apex-dormancy-section">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-bold text-rose-700 flex items-center gap-1">
              <Sparkles size={11} /> Phase 3b · Outbound dialer
            </div>
            <div className="font-bold text-sm mt-0.5">Wake dormant leads with an Apex callback</div>
            <div className="text-[11px] text-[var(--ink-3)]">
              Every 30 min, Apex queues outbound calls for leads that have gone quiet. Cross-call memory means the AI sounds informed, not cold.
            </div>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer shrink-0" data-testid="apex-dormancy-toggle">
            <input type="checkbox" className="sr-only peer"
              checked={!!s.dormancy_sweep_enabled}
              onChange={(e) => set({ dormancy_sweep_enabled: e.target.checked })}
            />
            <span className="w-9 h-5 bg-slate-300 peer-checked:bg-rose-500 rounded-full relative transition-colors">
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${s.dormancy_sweep_enabled ? "translate-x-4" : ""}`} />
            </span>
          </label>
        </div>

        {s.dormancy_sweep_enabled && (
          <div className="mt-3" data-testid="apex-dormancy-days-row">
            <label className="field-label flex items-center justify-between">
              <span>Wake up leads dormant for</span>
              <span className="text-rose-700 font-bold tabular-nums">
                {s.dormancy_days || 7} days
              </span>
            </label>
            <input type="range" min="3" max="30" step="1"
              value={s.dormancy_days || 7}
              onChange={(e) => set({ dormancy_days: Number(e.target.value) })}
              className="w-full accent-rose-600"
              data-testid="apex-dormancy-days"
            />
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-[var(--line)]">
        <button type="button" className="btn-money" onClick={onSave} disabled={saving} data-testid="apex-save">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {saving ? "Saving…" : "Save Apex settings"}
        </button>
        <button type="button" className="btn-outline" onClick={onTest} disabled={testing} data-testid="apex-test">
          {testing ? <Loader2 size={14} className="animate-spin" /> : <FlaskConical size={14} />}
          Dry-run Claude
        </button>
      </div>

      {testResult && (
        <div className="mt-4 p-3 rounded-lg bg-slate-50 border border-slate-200" data-testid="apex-test-result">
          <div className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-1 flex items-center gap-1">
            <CheckCircle2 size={11} /> Dry-run complete
          </div>
          {testResult.wrap_up && testResult.summary && Object.keys(testResult.summary).length > 0 ? (
            <>
              <div className="text-xs"><strong>AI verdict:</strong> {testResult.summary.ai_verdict} · <strong>Score:</strong> {testResult.summary.ai_score}</div>
              <div className="text-xs mt-1"><strong>Summary:</strong> {testResult.summary.ai_summary}</div>
              <div className="text-[11px] text-[var(--ink-3)] mt-2">
                Address: {testResult.summary.property_address || "—"} · Motivation: {testResult.summary.motivation || "—"} · Timeline: {testResult.summary.timeline || "—"} · Asking: ${(testResult.summary.asking_price || 0).toLocaleString()} · Condition: {testResult.summary.condition || "—"}
              </div>
            </>
          ) : (
            <div className="text-xs"><strong>Next AI line:</strong> "{testResult.next_ai_line}"</div>
          )}
        </div>
      )}
    </div>
  );
}



/* Voice picker section — Polly (free) vs ElevenLabs (Pro-tier premium).
   Handles the tier gate cleanly + preview-audio playback per voice. */
function ApexVoiceSection({ s, set }) {
  const provider = s.voice_provider || "polly";
  const [meta, setMeta] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    api.get("/apex/voices/elevenlabs")
      .then((r) => setMeta(r.data))
      .catch(() => setMeta({ configured: false, user_can_use: false, voices: [] }));
  }, []);

  const preview = async (voiceKey) => {
    setPreviewing(voiceKey);
    try {
      const r = await api.post(`/apex/voices/preview?voice_key=${voiceKey}`,
        null, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      if (audioRef.current) {
        audioRef.current.src = url;
        await audioRef.current.play();
      }
    } catch (err) {
      let msg = err?.response?.data?.detail;
      if (!msg && err?.response?.data instanceof Blob) {
        try { msg = JSON.parse(await err.response.data.text()).detail; } catch { /* ignore */ }
      }
      toast.error(msg || "Preview failed");
    } finally {
      setPreviewing(null);
    }
  };

  return (
    <>
      <label className="field-label">AI voice provider</label>
      <div className="grid grid-cols-2 gap-2 mb-3" data-testid="apex-voice-provider-group">
        <button type="button"
          onClick={() => set({ voice_provider: "polly" })}
          className={`text-left rounded-lg border p-3 transition ${
            provider === "polly"
              ? "border-[var(--primary)] bg-[var(--primary)]/10"
              : "border-[var(--line)] hover:border-[var(--primary)]"
          }`}
          data-testid="apex-voice-provider-polly"
        >
          <div className="flex items-center gap-2 mb-0.5">
            <div className="font-bold text-sm">Amazon Polly</div>
            <span className="text-[10px] uppercase tracking-widest font-bold bg-emerald-100 text-emerald-800 px-1.5 py-[1px] rounded">Free</span>
          </div>
          <div className="text-[11px] text-[var(--ink-3)]">Good quality · included in Twilio</div>
        </button>
        <button type="button"
          onClick={() => {
            if (!meta?.user_can_use) {
              toast.error("ElevenLabs voices require Pro tier or above");
              return;
            }
            set({ voice_provider: "elevenlabs" });
          }}
          disabled={!meta?.configured}
          className={`text-left rounded-lg border p-3 transition relative ${
            provider === "elevenlabs"
              ? "border-[var(--primary)] bg-[var(--primary)]/10"
              : "border-[var(--line)] hover:border-[var(--primary)]"
          } ${!meta?.configured || !meta?.user_can_use ? "opacity-60" : ""}`}
          data-testid="apex-voice-provider-elevenlabs"
        >
          <div className="flex items-center gap-2 mb-0.5">
            <div className="font-bold text-sm">ElevenLabs</div>
            <span className="text-[10px] uppercase tracking-widest font-bold bg-amber-100 text-amber-800 px-1.5 py-[1px] rounded flex items-center gap-0.5">
              {!meta?.user_can_use && <Lock size={9} />} Pro
            </span>
          </div>
          <div className="text-[11px] text-[var(--ink-3)]">Human-quality · nearly indistinguishable</div>
        </button>
      </div>

      {provider === "polly" && (
        <>
          <label className="field-label">Polly voice</label>
          <select className="input" value={s.voice}
            onChange={(e) => set({ voice: e.target.value })}
            data-testid="apex-voice-polly-select"
          >
            {POLLY_VOICES.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
          </select>
          <div className="text-[11px] text-[var(--ink-3)] mt-1">
            Neural voices included free via Twilio.
          </div>
        </>
      )}

      {provider === "elevenlabs" && meta?.voices?.length > 0 && (
        <>
          <label className="field-label">ElevenLabs voice</label>
          <div className="space-y-1.5" data-testid="apex-voice-elevenlabs-list">
            {meta.voices.map((v) => {
              const selected = (s.elevenlabs_voice_key || meta.default) === v.key;
              return (
                <div key={v.key}
                  className={`rounded-lg border p-2.5 flex items-center gap-2 cursor-pointer transition ${
                    selected
                      ? "border-[var(--primary)] bg-[var(--primary)]/10"
                      : "border-[var(--line)] hover:border-[var(--primary)]"
                  }`}
                  onClick={() => set({ elevenlabs_voice_key: v.key })}
                  data-testid={`apex-voice-eleven-${v.key}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm">{v.label}</div>
                    <div className="text-[11px] text-[var(--ink-3)]">{v.sub}</div>
                  </div>
                  <button type="button"
                    onClick={(e) => { e.stopPropagation(); preview(v.key); }}
                    disabled={previewing === v.key}
                    className="btn-outline btn-icon-only"
                    title={`Preview ${v.label}`}
                    data-testid={`apex-voice-preview-${v.key}`}
                  >
                    {previewing === v.key
                      ? <Loader2 size={14} className="animate-spin" />
                      : <Play size={13} />}
                  </button>
                </div>
              );
            })}
          </div>
          <audio ref={audioRef} className="hidden" />
          <div className="text-[11px] text-[var(--ink-3)] mt-2">
            ElevenLabs credits are billed to your account. Free tier is
            blocked from datacenter IPs — upgrade at elevenlabs.io/pricing.
          </div>
        </>
      )}
    </>
  );
}
